import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';

import * as help from './index';
import { ERRORS } from 'config';

/**
 * Prepare an error message
 *
 * @param {object} rule
 */
const prepare_error_message = (rule, field = 'field', customErrors = {}) => {
    let message = customErrors[`${field}.${rule.name}`];

    if (!message) {
        message = ERROR_MESSAGES[rule.name];
    }

    message = i18n._(message);

    message = message.replace('[attribute]', field);

    for (const [index, param] of rule.params.entries()) {
        message = message.replace(`[${index}]`, param);
    }

    return message;
};

/**
 * Extract the list of rules from a given string.
 *
 * @param {string} string
 */
const extract_rules = (string) => {
    let isRequired = false;

    const rules = string.split('|').map((rule) => {
        const params = rule.split(':');
        const name = params[0];

        if (name === 'required' || name === 'required_if') {
            isRequired = true;
        }

        return {
            name: name,
            params: params.length > 1 ? params[1].split(',') : [],
        };
    });

    return {
        rules,
        isRequired,
    };
};

/**
 * Run a series of checks against a string
 * to make sure it satifies each
 * requirement.
 *
 * @param {string} value
 * @param {string} rulesString
 * @param {object} data
 * @param {string} field
 */
export const validate = (
    value,
    rulesString,
    data = {},
    field,
    customErrors = {},
) => {
    if (!rulesString) {
        return null;
    }

    const { rules, isRequired } = extract_rules(rulesString);

    let error = null;

    if (!isRequired && !value.length) {
        return error;
    }

    for (let rule of rules) {
        let name = rule.name;
        let status = validation[name].apply(
            this,
            [value].concat(rule.params, data),
        );

        if (rule.name === 'required_if' && !data[rule.params[0]]) {
            break;
        }

        if (!status) {
            error = {
                message: prepare_error_message(rule, field, customErrors),
                rule: rule.name,
            };
            break;
        }
    }

    return error;
};

export const validateAll = (data, rules, customErrors = {}) => {
    const errors = {};

    Object.keys(data).forEach((key) => {
        const error = validate(data[key], rules[key], data, key, customErrors);
        if (error) {
            errors[key] = error.message;
        }
    });

    const valid = Object.keys(errors).every((errorKey) => {
        const error = errors[errorKey];
        return error === null || error.length === 0;
    });

    return {
        errors,
        valid,
    };
};

/**
 * List error messages
 */
const ERROR_MESSAGES = {
    email: ERRORS.email_not_valid,
    required: ERRORS.field_required,
    required_if: ERRORS.field_required,
    max: t`Must be less than [0] characters`,
    min: t`Must be at least [0] characters`,
    matches: t`The [attribute] must match the value of [0]`,
};

/**
 * List of available rules
 */
const validation = {
    required: (v) => v.length > 0,

    required_if: (value, fieldName, fieldValue, data) => {
        if (fieldValue instanceof Object) {
            data = fieldValue;
            fieldValue = null;
        }

        if (!fieldValue && data[fieldName] && !value.length) {
            return false;
        } else if (
            fieldValue &&
            data[fieldName] === fieldValue &&
            !value.length
        ) {
            return false;
        }

        return true;
    },

    matches: (value, field, data) => {
        return value === data[field];
    },

    email: (value) => {
        return help.str_validate_email(value);
    },

    max: (value, max) => {
        return value.length <= max;
    },

    min: (value, min) => {
        return value.length >= min;
    },
};
